'use client'

import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import ChevronIcon from '@src/quince-components/src/atoms/chevronIcon/ChevronIcon'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import LogoutIcon from '@src/quince-site-integrated/src/assets/logout.svg'
import TextButton from '@src/quince-site-integrated/src/components/TextButton'
import { getLinkPath, getUserDisplayName, logoutUser } from '@src/quince-site-integrated/src/utils'

import AccountWrapper from '../../components/Account/AccountWrapper'

import styles from './accountMenu.module.scss'

// @i18n-ignore
const TEXT_BUTTON_CATEGORY = 'login'

interface AccountPageData {
  hero: any
  menuList: any
  contact: any
  slug: string
  title: { title: string } | string
}

interface AccountMenuProps {
  data: {
    accountPage: AccountPageData
  }
}

const AccountMenu: React.FC<AccountMenuProps> = ({ data }) => {
  const { t } = useTranslation('common')
  const page = data.accountPage
  const menuList = page.menuList
  const { user } = useCurrentUser()

  const firstName = user?.customerMetadata?.firstName
  const lastName = user?.customerMetadata?.lastName
  const displayName = getUserDisplayName(firstName, lastName)

  const router = useRouter()
  const activeSlug = router.asPath

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  // Redirect desktop users to the orders/returns page
  useEffect(() => {
    if (!isMobile) {
      router.replace('/account/my-orders-returns')
    }
  }, [isMobile, router])

  return (
    <AccountWrapper heroImage={page.hero} menu={page.menuList} contact={page.contact} activeSlug={page.slug}>
      <div className={styles.container}>
        <div className={styles.welcomeText}>
          {t('hi')} {displayName || ''}
        </div>
        <div className={styles.emailText}>{user?.attributes?.email || ''}</div>
        <div className={styles.navWrapper}>
          <nav className={styles.nav}>
            {(menuList || []).map((menuItem: any) => {
              const isActive = activeSlug === getLinkPath(menuItem)
              return (
                <div
                  key={menuItem.id}
                  className={styles.navItem}
                  onClick={() => {
                    router.push(getLinkPath(menuItem))
                  }}
                >
                  <div className={styles.centeredDiv}>
                    {menuItem.logo && (
                      <img
                        src={menuItem.logo?.file?.url}
                        alt={menuItem.logo?.title || ''}
                        className={styles.navItemImage}
                        data-active={isActive ? 'true' : 'false'}
                        onClick={() => {
                          router.push(getLinkPath(menuItem))
                        }}
                        role="button"
                      />
                    )}
                    <TextButton
                      className={classNames(styles.navItemLink, {
                        [styles.navItemLinkActive]: isActive,
                      })}
                      to={getLinkPath(menuItem)}
                    >
                      {menuItem.text}
                    </TextButton>
                  </div>
                  <div className={styles.centeredDiv}>
                    <ChevronIcon />
                  </div>
                </div>
              )
            })}
          </nav>
          <TextButton
            className={styles.logout}
            onClick={logoutUser}
            trackParams={{
              trackInternal: true,
              category: TEXT_BUTTON_CATEGORY,
              action: 'account_logout',
              context: {
                login_email: user?.attributes?.email,
                lb_user_id: getlbUserId(),
              },
            }}
          >
            <LogoutIcon alt={t('logout')} className={styles.logoutIcon} />
            {t('logout_v1')}
          </TextButton>
        </div>
      </div>
    </AccountWrapper>
  )
}

export default AccountMenu
